import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { CircularProgress } from "@mui/material";
import { closeSnackbar } from "notistack";
import { useQueryClient } from "react-query";

import EmailField from "../../../Components/Form Fields/email-field";
import PrimaryButton from "../../../Components/Buttons/primary-button";

import OTPField from "../../../Components/Form Fields/otp-field";

import { useModalContext } from "../../../Contexts/ModalContext";
import { useAppUtilityContext } from "../../../Contexts/AppUtilityContext";

import {
  useEmailChangeRequestVerification,
  useEmailChangeVerification,
  useUpdateEmailAddress,
} from "../../../CustomHooks/Mutations";

import { APIQueryKeys, modalVariants } from "../../../Utils/constants";

import { dispatchErrorToast, dispatchInfoToast, dispatchSuccessToast } from "../../../Utils/helpers";

export default function renderEditEmail(modalContext, email) {
  const { updateModalComponent, updateModalFormDefaultValues, setModalVariant, setModalTitle } = modalContext;

  updateModalComponent(EditEmail);
  updateModalFormDefaultValues({ email });
  setModalVariant(modalVariants.formDialog);
  setModalTitle("Enter new email");
}

const EditEmail = () => {
  const resendOtpRef = useRef(false);

  const { handleSubmit, watch, getFieldState } = useFormContext();

  const [sentVerificationEmail, setSentVerificationEmail] = useState(false);

  const { setModalTitle, onModalClose, updateModalOnCloseCallBack } = useModalContext();

  const { onUserCredentialsUpdate } = useAppUtilityContext();

  const queryClient = useQueryClient();

  //Setting Dialog title on dependancy change
  useEffect(() => {
    if (!sentVerificationEmail) {
      setModalTitle("Enter new email");
    } else {
      setModalTitle("Verify your new email");
    }
  }, [sentVerificationEmail, setModalTitle]);

  const email = watch("email");
  const otp = watch("otp");

  const { isLoading: isRequestingEmailChangeVerification, mutate: RequestEmailChangeVerification } =
    useEmailChangeRequestVerification({
      onSuccess: () => {
        setSentVerificationEmail(true);
        if (resendOtpRef.current) {
          resendOtpRef.current = false;
          dispatchSuccessToast(`New verification code sent to ${email}`, { key: "requestEmailChange" });
        } else {
          dispatchSuccessToast(`Verification code sent to ${email}`);
          updateModalOnCloseCallBack(() =>
            dispatchInfoToast("Changes not saved", {
              key: "requestEmailChange",
              alignToAppContent: true,
            })
          );
        }
      },
      onError: (err) => {
        dispatchErrorToast(err?.response?.data?.error?.msg, { key: "requestEmailChange" });
        if (resendOtpRef.current) {
          resendOtpRef.current = false;
        }
      },
    });

  const { isLoading: isChangingEmailVerification, mutate: ChangeEmailVerification } = useEmailChangeVerification({
    onSuccess: (data) => {
      onUserCredentialsUpdate(data);
      queryClient.refetchQueries(APIQueryKeys.userAccount);
      UpdateEmail({ email });
    },
    onError: () =>
      dispatchErrorToast("Invalid verification code, please try again", {
        key: "changeEmail",
      }),
  });

  const { isLoading: isPatchingEmailAddress, mutate: UpdateEmail } = useUpdateEmailAddress({
    onSuccess: () => {
      dispatchSuccessToast("Email updated successfully", {
        key: "changeEmail",
        alignToAppContent: true,
      });
      onModalClose({ abortCallingOnCloseCallback: true });
    },
    onError: () =>
      dispatchErrorToast("Unable to update email address, please try again", {
        key: "changeEmail",
      }),
  });

  const isLoading = isRequestingEmailChangeVerification || isChangingEmailVerification || isPatchingEmailAddress;

  const onSubmit = () => {
    closeSnackbar();

    let payload = { email };

    if (!sentVerificationEmail || resendOtpRef.current) {
      RequestEmailChangeVerification(payload);
    } else {
      payload.phoneNumberCode = otp;
      ChangeEmailVerification(payload);
    }
  };

  const disableSubmitButton = !sentVerificationEmail ? !(email ?? "") || !getFieldState("email").isDirty : !(otp ?? "");

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <div className="modal-dialog-content">
        {!sentVerificationEmail ? (
          <>
            <EmailField autoFocus />
          </>
        ) : (
          <>
            <p className="modal-content-text mb-4">
              Please enter the code we sent to <span className="username-badge">{email}</span>
            </p>
            <div className="otp-field">
              <OTPField />
            </div>
            <button
              className="d-block modal-content-text text-decoration-underline mt-4 mx-auto"
              onClick={() => {
                resendOtpRef.current = true;
                onSubmit();
              }}
            >
              Didn’t receive an email? Resend code
            </button>
          </>
        )}
      </div>
      <div className="action-container split-equally">
        <PrimaryButton type="submit" disabled={disableSubmitButton || isLoading}>
          {!sentVerificationEmail ? "Verify" : "Update"} {isLoading && <CircularProgress />}
        </PrimaryButton>
        <PrimaryButton className="secondary-colors" type="button" onClick={onModalClose}>
          Discard
        </PrimaryButton>
      </div>
    </form>
  );
};
